import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EventSourcePolyfill } from "event-source-polyfill";
import { notification } from "../api/notification";

export interface AppNotification {
  id: number;
  user_id: number;
  object_id: number;
  object_type: string;
  active_from: string;
  read: boolean;
  date_read: string;
  title: string;
  title_description: string;
  relative_url: string;
  type: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  mobile_url: string | null;
  username: string;
}

export interface NotificationResponse {
  rows: AppNotification[];
  page: number;
  limit: number;
  total: number;
  has_next: boolean;
  has_previous: boolean;
}

export const useNotifications = () => {
  const queryClient = useQueryClient();

  const limit = 10;
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentUnread, setCurrentUnread] = useState(0);
  const [hasNew, setHasNew] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const prevUnreadRef = useRef(0);
  const currentPageRef = useRef(1);

  const fetchPage = async (pageNum: number) => {
    return queryClient.fetchQuery<NotificationResponse>({
      queryKey: ["notifications", pageNum],
      queryFn: async () => notification.getList({ page: pageNum, limit }),
      staleTime: 0
    });
  };

  const loadNotifications = async (reset = false) => {
    if(isLoading) return;
    setIsLoading(true);

    try {

      const pageToFetch = reset ? 1 : currentPageRef.current;

      if(reset) {
        currentPageRef.current = 1;
        setNotifications([]);
        setHasMore(true);
      } 
      const res = await fetchPage(pageToFetch);
      if (!res) return;
      setNotifications(prev =>
        reset ? res.rows : [...prev, ...res.rows]
      );
      setHasMore(res.has_next);
      if(res.has_next) {
        currentPageRef.current = pageToFetch + 1;
      }
    } catch (error) {
      console.error("Failed to load notifications", error);
    } finally {
      setIsLoading(false);
    }
  }

  const updateUnreadCount = (newUnread: number) => {
    if (newUnread > prevUnreadRef.current) {
      setHasNew(true);
    }
    prevUnreadRef.current = newUnread;
    setCurrentUnread(newUnread);
  };

  // SSE connection for live unread count
  useEffect(() => {
    const es = new EventSourcePolyfill(notification.streamApi, {
      withCredentials: true,
    });

    es.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.unreadCount !== undefined) {
          updateUnreadCount(data.unreadCount);
        }
      } catch (err) {
        console.error("Failed to parse SSE notification:", err);
      }
    };

    return () => es.close();
  }, [queryClient]);

  useEffect(() => {
    if (!hasNew) return;
    const timer = setTimeout(() => setHasNew(false), 1000);
    return () => clearTimeout(timer);
  }, [hasNew]);

  return {
    notifications,
    unreadCount: currentUnread,
    hasNew,
    isLoading,
    loadNotifications,
    hasMore,
  };
};